/**
 * DG SuperApp Bridge Service
 * Wraps the dg-super-app-bridge package for use in the Support MiniApp
 */

import {
  initializeMiniApp,
  getUserProfile,
  updateBackgroundColor,
  updateVisibleAppBar,
  closeMiniApp,
  capturePhotoBase64,
  pickPhotoBase64,
  selectFileBase64,
  selectMultiFileBase64,
  mobileScanner,
  canAuthenticateWithBiometrics,
  authenticateWithBiometrics,
  downloadAndShare,
  shareArrayBufferBridge,
  saveResource,
  openLink,
  requestCameraPermission,
  requestPhotoPermission,
  callFlutterHandler,
  type JavascriptBridgeModel,
  type Base64ContentModel,
  type AppFileModel,
  type InitializeMiniAppModel,
  type CanModel,
  type DGSuperAppFileType,
} from 'dg-super-app-bridge';

import type { Attachment, AudioRecordingResult, MicrophoneOptions, StartRecordingResult, StopRecordingResult } from '../types';

// Safe serialiser for whatever a Flutter bridge handler returns — including
// `undefined`, non-objects, objects with circular refs, etc. Used to surface
// the raw shape of a failure response in both console logs and the UI, so a
// user without Web Inspector access can still tell us what the bridge said.
function serializeBridgeRes(res: unknown): string {
  if (res === undefined) return 'undefined';
  if (res === null) return 'null';
  if (typeof res !== 'object') return String(res);
  try {
    return JSON.stringify(res, (_k, v) => {
      // Truncate large data fields so the string stays readable.
      if (typeof v === 'string' && v.length > 120) return `${v.slice(0, 117)}...`;
      if (v instanceof ArrayBuffer) return `ArrayBuffer(${v.byteLength})`;
      return v;
    });
  } catch {
    return Object.keys(res as object).join(',') || '[unserialisable]';
  }
}

class BridgeService {
  private initialized = false;
  private initData: InitializeMiniAppModel | null = null;
  private userProfile: { initData: string } | null = null;

  /**
   * Initialize the MiniApp with DG SuperApp
   */
  async initialize(): Promise<InitializeMiniAppModel | null> {
    if (this.initialized && this.initData) {
      return this.initData;
    }

    try {
      const result = await initializeMiniApp() as JavascriptBridgeModel<InitializeMiniAppModel> | null;

      if (result?.success && result.data) {
        this.initialized = true;
        this.initData = result.data;

        // Set app bar color to match support system theme
        await this.setAppBarStyle('Technical Support', '#FFFFFF', '#1e40af');

        return result.data;
      }

      return null;
    } catch (error) {
      console.error('Failed to initialize MiniApp:', error);
      return null;
    }
  }

  /**
   * Check if running inside DG SuperApp
   */
  get isInSuperApp(): boolean {
    return this.initData?.isDGSuperApp ?? false;
  }

  /**
   * Check if running in iframe-embedded mode (detected via `?mode=embedded`
   * URL param on the host page). Used by chat primitives to switch between
   * role-based (standalone) and identity-based (embedded) bubble positioning.
   */
  get isEmbedded(): boolean {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('mode') === 'embedded';
  }

  /**
   * Get device info
   */
  get deviceInfo(): string | undefined {
    return this.initData?.deviceInfo;
  }

  /**
   * Get locale
   */
  get locale(): string {
    return this.initData?.locale ?? 'en';
  }

  /**
   * Get safe area padding
   */
  get safeArea(): {
    top: number;
    bottom: number;
    left: number;
    right: number;
  } {
    return {
      top: this.initData?.viewPaddingTop ?? 0,
      bottom: this.initData?.viewPaddingBottom ?? 0,
      left: this.initData?.viewPaddingLeft ?? 0,
      right: this.initData?.viewPaddingRight ?? 0,
    };
  }

  /**
   * Get available features
   */
  get availableFeatures(): string[] {
    return this.initData?.features ?? [];
  }

  /**
   * Check if a feature is available
   */
  hasFeature(feature: string): boolean {
    return this.availableFeatures.includes(feature);
  }

  /**
   * Parse deviceId from initData string (newline-delimited key=value pairs)
   */
  private parseDeviceIdFromInitData(initData: string): string | null {
    const lines = initData.split('\n');
    for (const line of lines) {
      if (line.startsWith('deviceId=')) {
        const value = line.substring('deviceId='.length).trim();
        return value || null;
      }
    }
    return null;
  }

  /**
   * Extract deviceId from getUserProfile initData, with InitializeMiniAppModel fallback.
   * getUserProfile<T>() returns T | null directly — so we use the wrapper shape as T.
   * Returns null if neither source has a deviceId.
   */
  async extractDeviceId(): Promise<string | null> {
    try {
      // getUserProfile returns the full response object as T | null
      const response = await getUserProfile<{ success: boolean; data: { initData: string } }>();
      if (response?.data?.initData) {
        const deviceId = this.parseDeviceIdFromInitData(response.data.initData);
        if (deviceId) {
          return deviceId;
        }
      }
    } catch (error) {
      console.error('Failed to get user profile for deviceId extraction:', error);
    }

    // Fallback to InitializeMiniAppModel
    if (this.initData?.deviceId) {
      return this.initData.deviceId;
    }

    return null;
  }

  /**
   * Load user profile from DG SuperApp
   */
  async loadUserProfile(): Promise<{ initData: string } | null> {
    try {
      // getUserProfile returns the full response object as T | null
      const response = await getUserProfile<{ success: boolean; data: { initData: string } }>();
      if (response?.data) {
        this.userProfile = response.data;
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Failed to load user profile:', error);
      return null;
    }
  }

  /**
   * Get cached user profile
   */
  get currentUser(): { initData: string } | null {
    return this.userProfile;
  }

  /**
   * Raw signed initData blob (newline-delimited key=value lines, including the
   * final `hash=` line) exactly as the SuperApp bridge hands it to us. This is
   * what the server-side HMAC-SHA-512 verifier checks. Returns null outside the
   * SuperApp or if the bridge yields no profile.
   */
  async getRawInitData(): Promise<string | null> {
    const profile = await this.loadUserProfile();
    return profile?.initData ?? null;
  }

  /**
   * Update app bar style
   */
  async setAppBarStyle(
    title?: string,
    titleColor?: string,
    backgroundColor?: string
  ): Promise<void> {
    try {
      await updateBackgroundColor(title, titleColor, backgroundColor);
    } catch (error) {
      console.error('Failed to update app bar style:', error);
    }
  }

  /**
   * Show/hide the default app bar
   */
  async setAppBarVisible(visible: boolean): Promise<void> {
    try {
      await updateVisibleAppBar(visible);
    } catch (error) {
      console.error('Failed to toggle app bar visibility:', error);
    }
  }

  /**
   * Close the MiniApp
   */
  async close(): Promise<void> {
    try {
      await closeMiniApp();
    } catch (error) {
      console.error('Failed to close MiniApp:', error);
    }
  }

  /**
   * Close the MiniApp with a native exit confirmation dialog (handled by SuperApp).
   */
  async closeWithConfirm(): Promise<void> {
    try {
      await callFlutterHandler('closeMiniAppWithExitDialog');
    } catch (error) {
      console.error('Failed to close MiniApp with exit dialog:', error);
    }
  }

  /**
   * Request camera permission
   */
  async requestCameraAccess(): Promise<boolean> {
    try {
      const result = await requestCameraPermission();
      return result?.success ?? false;
    } catch (error) {
      console.error('Failed to request camera permission:', error);
      return false;
    }
  }

  /**
   * Request photo library permission
   */
  async requestPhotoAccess(): Promise<boolean> {
    try {
      const result = await requestPhotoPermission();
      return result?.success ?? false;
    } catch (error) {
      console.error('Failed to request photo permission:', error);
      return false;
    }
  }

  /**
   * Capture a photo using camera
   */
  async capturePhoto(): Promise<Attachment | null> {
    try {
      const result = await capturePhotoBase64() as JavascriptBridgeModel<Base64ContentModel> | null;

      if (result?.success && result.data?.base64) {
        return {
          type: 'image',
          base64: result.data.base64,
          fileName: `photo_${Date.now()}.jpg`,
          extension: 'jpg',
        };
      }
      return null;
    } catch (error) {
      console.error('Failed to capture photo:', error);
      return null;
    }
  }

  /**
   * Pick a photo from gallery
   */
  async pickPhoto(): Promise<Attachment | null> {
    try {
      const result = await pickPhotoBase64() as JavascriptBridgeModel<Base64ContentModel> | null;

      if (result?.success && result.data?.base64) {
        return {
          type: 'image',
          base64: result.data.base64,
          fileName: `gallery_${Date.now()}.jpg`,
          extension: 'jpg',
        };
      }
      return null;
    } catch (error) {
      console.error('Failed to pick photo:', error);
      return null;
    }
  }

  /**
   * Select a file
   */
  async selectFile(): Promise<Attachment | null> {
    try {
      const result = await selectFileBase64() as JavascriptBridgeModel<AppFileModel> | null;

      if (result?.success && result.data?.base64Data) {
        return {
          type: 'document',
          base64: result.data.base64Data,
          fileName: result.data.fileName,
          extension: result.data.extension,
        };
      }
      return null;
    } catch (error) {
      console.error('Failed to select file:', error);
      return null;
    }
  }

  /**
   * Select multiple files
   */
  async selectMultipleFiles(): Promise<Attachment[]> {
    try {
      const result = await selectMultiFileBase64() as JavascriptBridgeModel<AppFileModel[]> | null;

      if (result?.success && result.data) {
        return result.data
          .filter(file => file.base64Data)
          .map(file => ({
            type: 'document' as const,
            base64: file.base64Data!,
            fileName: file.fileName,
            extension: file.extension,
          }));
      }
      return [];
    } catch (error) {
      console.error('Failed to select multiple files:', error);
      return [];
    }
  }

  /**
   * Scan QR code
   */
  async scanQRCode(): Promise<string | null> {
    try {
      const result = await mobileScanner() as JavascriptBridgeModel<string> | null;

      if (result?.success && result.data) {
        return result.data;
      }
      return null;
    } catch (error) {
      console.error('Failed to scan QR code:', error);
      return null;
    }
  }

  /**
   * Check if biometric authentication is available
   */
  async canUseBiometrics(): Promise<boolean> {
    try {
      const result = await canAuthenticateWithBiometrics() as JavascriptBridgeModel<CanModel> | null;
      return result?.data?.can ?? false;
    } catch (error) {
      console.error('Failed to check biometrics availability:', error);
      return false;
    }
  }

  /**
   * Authenticate with biometrics
   */
  async authenticateWithBiometrics(): Promise<boolean> {
    try {
      const result = await authenticateWithBiometrics() as JavascriptBridgeModel<CanModel> | null;
      return result?.data?.can ?? false;
    } catch (error) {
      console.error('Failed to authenticate with biometrics:', error);
      return false;
    }
  }

  /**
   * Download and share a file
   */
  async shareFile(
    fileUrl: string,
    fileType: DGSuperAppFileType,
    extension: string,
    fileName: string
  ): Promise<boolean> {
    try {
      // `downloadAndShare` resolves with `{ success, message, data }` shape
      // for both success and soft failures (iOS share-sheet bugs, user
      // cancels, permission denied) — it does NOT throw. Mirror what
      // `shareBase64` does so callers can detect the failure and fall back.
      const result = (await downloadAndShare(fileUrl, fileType, extension, fileName)) as
        | { success?: boolean }
        | undefined
        | null;
      return result?.success ?? false;
    } catch (error) {
      console.error('Failed to share file:', error);
      return false;
    }
  }

  /**
   * Share content from base64 data
   */
  async shareBase64(
    data: Uint8Array | ArrayBuffer | number[],
    fileName: string,
    fileType: DGSuperAppFileType
  ): Promise<boolean> {
    try {
      const result = await shareArrayBufferBridge(data, { fileName, fileType });
      return result?.success ?? false;
    } catch (error) {
      console.error('Failed to share base64 content:', error);
      return false;
    }
  }

  /**
   * Save a file straight to the device (no share sheet, no inline preview).
   * Used for the CSV template — the SuperApp's iOS share sheet has a known bug
   * (sharePositionOrigin) that falls back to an inline file view, so we save
   * directly instead. `saveResource` resolves void/null and does not throw on
   * soft failures; a thrown error is the only hard-failure signal (→ false).
   */
  async saveFile(
    base64Data: string,
    fileName: string,
    extension: string,
    fileType: DGSuperAppFileType = 'anyFile'
  ): Promise<boolean> {
    try {
      await saveResource(fileName, base64Data, fileType, extension);
      return true;
    } catch (error) {
      console.error('Failed to save file:', error);
      return false;
    }
  }

  // ==========================================
  // Audio Recording (via Flutter bridge handlers)
  // ==========================================

  private audioInitialized = false;

  /**
   * Initialize the audio recorder engine
   */
  async initializeAudioRecorder(): Promise<boolean> {
    try {
      const res = await callFlutterHandler('microphone.initialize');
      const success = res && typeof res === 'object' && 'success' in res ? res.success : true;
      if (success) {
        this.audioInitialized = true;
      }
      return success;
    } catch (error) {
      console.error('Failed to initialize audio recorder:', error);
      return false;
    }
  }

  /**
   * Check if audio recorder is initialized
   */
  get isAudioReady(): boolean {
    return this.audioInitialized;
  }

  /**
   * Get microphone permission status
   */
  async getMicPermissionStatus(): Promise<string> {
    try {
      const res = await callFlutterHandler('microphone.getPermissionStatus');
      return res?.data?.status ?? 'unknown';
    } catch (error) {
      console.error('Failed to get mic permission status:', error);
      return 'unknown';
    }
  }

  /**
   * Request microphone permission
   */
  async requestMicPermission(): Promise<boolean> {
    try {
      const res = await callFlutterHandler('microphone.requestPermission');
      return res?.success ?? false;
    } catch (error) {
      console.error('Failed to request mic permission:', error);
      return false;
    }
  }

  /**
   * Start audio recording. Returns a discriminated result so the caller can
   * show the user a specific actionable message instead of a single opaque
   * "Failed to start recording".
   *
   * NOTE: We intentionally do NOT pre-check mic permission here. The Flutter
   * native layer behind `microphone.start` already handles the OS permission
   * prompt on both iOS and Android, and its handling is more accurate than
   * our JS-side mapping of bridge status strings (Android in particular has
   * states like `deniedForever`/`limited` that don't compare as `=== 'granted'`
   * and would wrongly short-circuit us before `microphone.start` ever runs).
   * This matches the vendor sample's Microphone component pattern and matches
   * the behavior that worked on Cloudflare before commit 31929b1 introduced
   * the now-removed pre-check wall.
   */
  async startRecording(options?: MicrophoneOptions): Promise<StartRecordingResult> {
    try {
      // Lazy-init ONCE — matches CF-era working code and the vendor sample,
      // which calls microphone.initialize a single time (via their Initialize
      // Engine button) and never again for the session. Re-initialising
      // before every start destabilises the native recorder on some plugin
      // builds (the plugin doesn't expect a second init while a session is
      // live) and was correlating with null responses from microphone.stop.
      if (!this.audioInitialized) {
        const initialized = await this.initializeAudioRecorder();
        if (!initialized) {
          return { ok: false, reason: 'init-failed' };
        }
      }

      const attempts: Array<MicrophoneOptions | undefined> = [];
      if (options) {
        attempts.push(options);

        // Some host builds reject opus; retry with AAC, which is what the vendor sample uses.
        if (options.encoder === 'opus') {
          attempts.push({ ...options, encoder: 'aac' });
        }

        // Final fallback for older host builds that only accept default recorder settings.
        attempts.push(undefined);
      } else {
        attempts.push(undefined);
      }

      let lastRejectDetail: string | undefined;
      for (const attemptOptions of attempts) {
        const res = await callFlutterHandler(
          'microphone.start',
          attemptOptions ? [attemptOptions] : []
        );
        const success = res && typeof res === 'object' && 'success' in res ? res.success : true;

        if (success) {
          return { ok: true };
        }

        lastRejectDetail = res?.message ?? `rejected options=${JSON.stringify(attemptOptions ?? 'default')}`;
        console.error('microphone.start rejected by host:', {
          options: attemptOptions ?? 'default',
          message: res?.message,
          data: res?.data,
        });
      }

      return { ok: false, reason: 'host-rejected', detail: lastRejectDetail };
    } catch (error) {
      console.error('Failed to start recording:', error);
      return { ok: false, reason: 'exception', detail: (error as Error)?.message };
    }
  }

  /**
   * Stop audio recording and get the result
   */
  async stopRecording(returnFormat: 'base64' | 'buffer' = 'base64'): Promise<StopRecordingResult> {
    try {
      let res = await callFlutterHandler('microphone.stop', [{ returnFormat }]);

      // If the first call returns literal null, the bridge handler may have
      // a different signature than [{returnFormat}] on this plugin build
      // (vs. {success:false} which is a real "I received your call but
      // failed" response). Retry once with no arguments. Safe because a null
      // return means the plugin didn't consume our call — the recording
      // session is still live.
      if (res === null || res === undefined) {
        console.warn('microphone.stop([{returnFormat}]) returned null, retrying with no args');
        res = await callFlutterHandler('microphone.stop', []);
      }

      if (!res?.success) {
        const full = serializeBridgeRes(res);
        console.error('microphone.stop reported failure, full response:', full);
        return { ok: false, reason: 'stop-failed', detail: res?.message ?? full };
      }

      if (!res.data) {
        console.error('microphone.stop succeeded but no data field. Full response:', serializeBridgeRes(res));
        return { ok: false, reason: 'no-data' };
      }

      const data = res.data as AudioRecordingResult;
      const hasPayload = !!data.base64 || !!data.buffer;
      const hasContent = (data.fileSize ?? 0) > 0 && (data.duration ?? 0) > 0;

      if (!hasPayload || !hasContent) {
        // Most common cause: OS-level mic permission was silently denied while
        // the bridge optimistically reported microphone.start success. Query
        // permission status so the caller can point the user at the right
        // OS settings screen instead of guessing.
        let permissionStatus: string | undefined;
        try {
          permissionStatus = await this.getMicPermissionStatus();
        } catch {
          // best-effort; leave undefined
        }
        console.error('microphone.stop returned empty recording:', {
          duration: data.duration,
          fileSize: data.fileSize,
          hasBase64: !!data.base64,
          hasBuffer: !!data.buffer,
          mimeType: data.mimeType,
          permissionStatus,
        });
        return {
          ok: false,
          reason: 'empty-recording',
          detail: `size=${data.fileSize ?? 0}B duration=${data.duration ?? 0}s`,
          permissionStatus,
        };
      }

      return { ok: true, result: data };
    } catch (error) {
      console.error('Failed to stop recording:', error);
      return { ok: false, reason: 'exception', detail: (error as Error)?.message };
    }
  }

  /**
   * Convert an AudioRecordingResult to an Attachment suitable for upload
   */
  audioResultToAttachment(result: AudioRecordingResult): Attachment | null {
    let base64Data: string | undefined;

    if (result.base64) {
      base64Data = result.base64;
    } else if (result.buffer) {
      // Convert buffer to base64
      const bytes = Array.isArray(result.buffer)
        ? new Uint8Array(result.buffer)
        : new Uint8Array(result.buffer);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      base64Data = btoa(binary);
    }

    if (!base64Data) return null;

    const mimeType = result.mimeType || 'audio/aac';
    const ext = this.getAudioExtension(mimeType);
    return {
      type: 'audio',
      base64: base64Data,
      fileName: `voice_${Date.now()}.${ext}`,
      extension: ext,
      mimeType,
      duration: result.duration,
    };
  }

  private getAudioExtension(mimeType: string): string {
    const normalizedMimeType = mimeType.toLowerCase().split(';')[0].trim();

    if (normalizedMimeType === 'audio/ogg') return 'ogg';
    if (normalizedMimeType === 'audio/mp4' || normalizedMimeType === 'audio/x-m4a') return 'm4a';
    if (normalizedMimeType === 'audio/mpeg') return 'mp3';
    if (normalizedMimeType === 'audio/wav' || normalizedMimeType === 'audio/x-wav') return 'wav';
    if (normalizedMimeType === 'audio/webm') return 'webm';
    if (normalizedMimeType === 'audio/aac') return 'aac';

    return normalizedMimeType.split('/')[1] || 'aac';
  }

  /**
   * Open a link
   */
  async openExternalLink(
    url: string,
    mode: 'inAppWebView' | 'inAppBrowserView' | 'externalApplication' = 'externalApplication'
  ): Promise<void> {
    try {
      await openLink(url, mode);
    } catch (error) {
      console.error('Failed to open link:', error);
    }
  }

  /** Native copy-to-clipboard via the SuperApp bridge (web clipboard APIs are
   *  blocked in the iOS webview). `callFlutterHandler` SPREADS its second
   *  argument as positional args into `flutter_inappwebview.callHandler`, so it
   *  MUST be an array — the native `copyToClipboard` handler reads `args[0]` as
   *  the string to copy. The old code passed a bare object, which slice-spread
   *  to `[]` and invoked the handler with no text (copied nothing) while still
   *  resolving — hence the "reports success but clipboard is empty" bug. */
  async copyToClipboard(text: string): Promise<boolean> {
    if (!this.isInSuperApp) return false;
    try {
      await callFlutterHandler('copyToClipboard', [text]);
      return true;
    } catch {
      return false;
    }
  }
}

// Export singleton instance
export const bridgeService = new BridgeService();
